import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { BarChart3, LogOut, MessageSquare, Pause, Play, Users } from "lucide-react";

const NAV_ITEMS = [
  { to: "/", label: "Conversations", icon: MessageSquare, testId: "nav-conversations", end: true },
  { to: "/leads", label: "Leads", icon: Users, testId: "nav-leads" },
  { to: "/stats", label: "Statistiques", icon: BarChart3, testId: "nav-stats" },
];

export default function DashboardLayout() {
  const { logout } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const fetchStatus = () =>
      api.get("/settings/agent-status").then((res) => setPaused(res.data.paused)).catch(() => {});
    fetchStatus();
    const interval = setInterval(fetchStatus, 15000);
    return () => clearInterval(interval);
  }, []);

  const togglePause = async () => {
    try {
      const res = await api.post(paused ? "/settings/resume" : "/settings/pause");
      setPaused(res.data.paused);
      if (res.data.paused) {
        toast.warning("Agent en pause : plus aucune réponse automatique n'est envoyée.");
      } else {
        toast.success("Agent réactivé : les réponses automatiques reprennent.");
      }
    } catch {
      toast.error("Action impossible pour le moment.");
    }
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm transition-colors duration-200 ${
      isActive
        ? "bg-secondary text-primary font-semibold"
        : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
    }`;

  return (
    <div className="h-screen flex flex-col md:flex-row bg-background overflow-hidden">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex md:w-64 flex-col border-r border-border bg-background px-4 py-6">
        <div className="px-4 mb-8">
          <h1 className="font-serif text-2xl leading-tight" data-testid="sidebar-brand-name">{branding.name || "Dashboard"}</h1>
          <p className="text-xs tracking-widest uppercase text-muted-foreground mt-1">
            {[branding.city, branding.country].filter(Boolean).join(" · ")}
          </p>
        </div>
        <nav className="flex-1 space-y-1">
          {NAV_ITEMS.map(({ to, label, icon: Icon, testId, end }) => (
            <NavLink key={to} to={to} end={end} className={linkClass} data-testid={testId}>
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <button
          onClick={togglePause}
          data-testid="pause-agent-button"
          className={`flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm mb-1 transition-colors duration-200 ${
            paused
              ? "bg-red-600/10 text-red-700 hover:bg-red-600/20 font-semibold"
              : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
          }`}
        >
          {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          {paused ? "Réactiver l'agent" : "Mettre l'agent en pause"}
        </button>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors duration-200"
        >
          <LogOut className="h-4 w-4" />
          Se déconnecter
        </button>
      </aside>

      {/* Header mobile */}
      <header className="md:hidden flex items-center justify-between border-b border-border px-4 py-3 bg-background">
        <div>
          <h1 className="font-serif text-lg leading-none">{branding.name || "Dashboard"}</h1>
          <p className="text-[10px] tracking-widest uppercase text-muted-foreground mt-0.5">
            {[branding.city, branding.country].filter(Boolean).join(" · ")}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={togglePause}
            data-testid="pause-agent-button-mobile"
            className={`p-2 ${paused ? "text-red-600" : "text-muted-foreground"}`}
            aria-label={paused ? "Réactiver l'agent" : "Mettre l'agent en pause"}
          >
            {paused ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
          </button>
          <button
            onClick={handleLogout}
            data-testid="logout-button-mobile"
            className="text-muted-foreground p-2"
            aria-label="Se déconnecter"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-hidden pb-16 md:pb-0 flex flex-col">
        {paused && (
          <div
            data-testid="agent-paused-banner"
            className="bg-red-600 text-white text-sm text-center py-2 px-4 shrink-0"
          >
            ⏸ Agent en pause — aucune réponse automatique n'est envoyée. Les messages entrants restent reçus et visibles.{" "}
            <button onClick={togglePause} className="underline font-semibold" data-testid="banner-resume-button">
              Réactiver
            </button>
          </div>
        )}
        <div className="flex-1 overflow-hidden">
          <Outlet />
        </div>
      </main>

      {/* Nav mobile en bas */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 border-t border-border bg-card flex justify-around py-2 z-40">
        {NAV_ITEMS.map(({ to, label, icon: Icon, testId, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`${testId}-mobile`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 px-4 py-1 text-[11px] ${
                isActive ? "text-primary font-semibold" : "text-muted-foreground"
              }`
            }
          >
            <Icon className="h-5 w-5" />
            {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
